import React, { useCallback, useEffect, useState,useMemo } from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  AssessmentOutlined,
  BusinessOutlined,
  CloseOutlined,
  CloudDoneOutlined,
  CloudOffOutlined,
  DirectionsTransitOutlined,
  FactoryOutlined,
  FilterAltOutlined,
  Inventory2Outlined,
  LocationOnOutlined,
  RefreshOutlined,
  SearchOutlined,
  StoreOutlined,
} from "@mui/icons-material";

import { getMasterStockStatusList } from "../../services/StockTransferService";


/* ============================================================
   Constants
============================================================ */

const DEFAULT_PAGE_SIZE = 50;

const PAGE_SIZE_OPTIONS = [25, 50, 100, 150, 200];

const STAGE_CONFIG = {
  factory: {
    label: "Factory",
    color: "default",
    icon: FactoryOutlined,
  },

  in_transit: {
    label: "In Transit",
    color: "warning",
    icon: DirectionsTransitOutlined,
  },

  manufacturer: {
    label: "Manufacturer",
    color: "info",
    icon: BusinessOutlined,
  },

  dealer: {
    label: "Dealer",
    color: "secondary",
    icon: StoreOutlined,
  },

  owner: {
    label: "Owner",
    color: "success",
    icon: Inventory2Outlined,
  },
};


/* ============================================================
   Helper Functions
============================================================ */

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};


const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};


const getStageConfig = (stage) => {
  return (
    STAGE_CONFIG[stage] || {
      label: stage || "Unknown",
      color: "default",
      icon: LocationOnOutlined,
    }
  );
};


const getEsimStatusColor = (status) => {
  if (!status) {
    return "default";
  }

  switch (status.toLowerCase()) {
    case "active":
      return "success";

    case "inactive":
      return "default";

    case "expired":
      return "error";

    case "suspend":
      return "warning";

    case "limited connectivity":
      return "warning";

    default:
      return "default";
  }
};


const getCommercialStatusColor = (status) => {
  if (!status) {
    return "default";
  }

  switch (status.toLowerCase()) {
    case "active":
      return "success";

    case "expired":
      return "error";

    case "suspended":
      return "warning";

    case "inactive":
      return "default";

    default:
      return "default";
  }
};


/* ============================================================
   Summary Card
============================================================ */

const SummaryCard = ({
  title,
  value,
  icon,
  subtitle,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        transition: "all 0.2s ease",

        "&:hover": {
          boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
          transform: "translateY(-1px)",
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {value}
          </Typography>

          {subtitle && (
            <Typography
              variant="caption"
              color="text.secondary"
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "action.hover",
          }}
        >
          {icon}
        </Box>
      </Stack>
    </Paper>
  );
};


/* ============================================================
   Main Component
============================================================ */

const StockStatusReport = () => {
  /* ----------------------------------------------------------
     State
  ---------------------------------------------------------- */

  const [rows, setRows] = useState([]);

  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [search, setSearch] = useState("");

  const [filtersOpen, setFiltersOpen] = useState(false);

  const [filters, setFilters] = useState({
    id: "",
    uid: "",
    imei: "",
    iccid_primary: "",
    device_model_code: "",
  });

  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });


  /* ----------------------------------------------------------
     Summary
  ---------------------------------------------------------- */

  const summary = useMemo(() => {
    const result = {
      factory: 0,
      in_transit: 0,
      manufacturer: 0,
      dealer: 0,
      owner: 0,
      online: 0,
      offline: 0,
    };

    rows.forEach((item) => {
      const stage = item?.location?.stage;

      if (stage && result[stage] !== undefined) {
        result[stage] += 1;
      }

      if (item?.online) {
        result.online += 1;
      } else {
        result.offline += 1;
      }
    });

    return result;
  }, [rows]);


  /* ==========================================================
     Snackbar
  ========================================================== */

  const showSnackbar = useCallback(
    (message, severity = "error") => {
      setSnackbar({
        open: true,
        message,
        severity,
      });
    },
    []
  );


  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };


  /* ==========================================================
     API
  ========================================================== */

  const fetchStockStatus = useCallback(
    async ({
      requestedPage = page,
      requestedPageSize = pageSize,
    } = {}) => {
      setLoading(true);

      try {
        const cleanedFilters = Object.entries(filters).reduce(
          (accumulator, [key, value]) => {
            const trimmedValue = String(value || "").trim();

            if (trimmedValue) {
              accumulator[key] = trimmedValue;
            }

            return accumulator;
          },
          {}
        );

        const payload = {
          filters: cleanedFilters,
          search: search.trim(),
          include_deleted: includeDeleted,
          page: requestedPage + 1,
          page_size: Math.min(requestedPageSize, 200),
        };

        const response = await getMasterStockStatusList(payload);

        const data = response?.data || {};

        setRows(
          Array.isArray(data?.results)
            ? data.results
            : []
        );

        setTotal(
          Number(data?.total || 0)
        );

      } catch (error) {
        console.error(
          "Master stock status API error:",
          error
        );

        const status = error?.response?.status;

        if (status === 403) {
          showSnackbar(
            "Access denied. You do not have permission to view this report.",
            "error"
          );
        } else {
          showSnackbar(
            error?.response?.data?.detail ||
              error?.response?.data?.message ||
              "Failed to load master stock status report.",
            "error"
          );
        }

        setRows([]);
        setTotal(0);

      } finally {
        setLoading(false);
      }
    },
    [
      filters,
      includeDeleted,
      page,
      pageSize,
      search,
      showSnackbar,
    ]
  );


  /* ==========================================================
     Initial Load
  ========================================================== */

  useEffect(() => {
    fetchStockStatus({
      requestedPage: page,
      requestedPageSize: pageSize,
    });
  }, [
    fetchStockStatus,
    page,
    pageSize,
  ]);


  /* ==========================================================
     Search
  ========================================================== */

  const handleSearch = () => {
    setPage(0);

    fetchStockStatus({
      requestedPage: 0,
      requestedPageSize: pageSize,
    });
  };


  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };


  /* ==========================================================
     Filter
  ========================================================== */

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };


  const handleApplyFilters = () => {
    setPage(0);

    fetchStockStatus({
      requestedPage: 0,
      requestedPageSize: pageSize,
    });
  };


  const handleClearFilters = () => {
    const clearedFilters = {
      id: "",
      uid: "",
      imei: "",
      iccid_primary: "",
      device_model_code: "",
    };

    setFilters(clearedFilters);

    setSearch("");

    setIncludeDeleted(false);

    setPage(0);

    /*
     * Direct API call because state updates are asynchronous.
     */
    const payload = {
      filters: {},
      search: "",
      include_deleted: false,
      page: 1,
      page_size: pageSize,
    };

    const loadClearedData = async () => {
      setLoading(true);

      try {
        const response =
          await getMasterStockStatusList(payload);

        const data = response?.data || {};

        setRows(
          Array.isArray(data?.results)
            ? data.results
            : []
        );

        setTotal(
          Number(data?.total || 0)
        );

      } catch (error) {
        showSnackbar(
          error?.response?.data?.detail ||
            error?.response?.data?.message ||
            "Unable to reset filters.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    loadClearedData();
  };


  /* ==========================================================
     Pagination
  ========================================================== */

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };


  const handleRowsPerPageChange = (event) => {
    const newPageSize =
      parseInt(event.target.value, 10);

    setPageSize(newPageSize);
    setPage(0);
  };


  /* ==========================================================
     Refresh
  ========================================================== */

  const handleRefresh = () => {
    fetchStockStatus({
      requestedPage: page,
      requestedPageSize: pageSize,
    });
  };


  /* ==========================================================
     Render
  ========================================================== */

  return (
    <Box
      sx={{
        width: "100%",
        p: {
          xs: 1.5,
          sm: 2,
          md: 3,
        },
      }}
    >

      {/* ======================================================
          Header
      ====================================================== */}

      <Box
        sx={{
          mb: 2.5,
        }}
      >
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "flex-start",
            md: "center",
          }}
          spacing={2}
        >
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.2}
              sx={{ mb: 0.5 }}
            >
              <AssessmentOutlined
                color="primary"
                sx={{
                  fontSize: 30,
                }}
              />

              <Typography
                variant="h5"
                fontWeight={700}
              >
                Master Stock Status
              </Typography>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Complete unit-level stock lifecycle,
              location and connectivity report.
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={1}
          >
            <Tooltip title="Refresh report">
              <span>
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <RefreshOutlined />
                </IconButton>
              </span>
            </Tooltip>

            <Button
              variant="outlined"
              startIcon={
                <FilterAltOutlined />
              }
              onClick={() =>
                setFiltersOpen((prev) => !prev)
              }
            >
              Filters
            </Button>
          </Stack>
        </Stack>
      </Box>


      {/* ======================================================
          Summary Cards
      ====================================================== */}

      <Grid
        container
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Units"
            value={total.toLocaleString("en-IN")}
            subtitle="All matching records"
            icon={
              <Inventory2Outlined
                color="primary"
              />
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Factory"
            value={summary.factory}
            subtitle="Current page"
            icon={
              <FactoryOutlined
                color="action"
              />
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="In Transit"
            value={summary.in_transit}
            subtitle="Current page"
            icon={
              <DirectionsTransitOutlined
                color="warning"
              />
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Online"
            value={summary.online}
            subtitle="Live connection status"
            icon={
              <CloudDoneOutlined
                color="success"
              />
            }
          />
        </Grid>
      </Grid>


      {/* ======================================================
          Search Bar
      ====================================================== */}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 2,
          mb: 2,
        }}
      >
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          spacing={1.5}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Search UID, IMEI, ICCID, ESN or MSISDN..."
            value={search}
            onChange={(event) =>
              setSearch(event.target.value)
            }
            onKeyDown={handleSearchKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined
                    color="action"
                  />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            onClick={handleSearch}
            startIcon={<SearchOutlined />}
            sx={{
              minWidth: 120,
            }}
          >
            Search
          </Button>
        </Stack>
      </Paper>


      {/* ======================================================
          Filters
      ====================================================== */}

      <Collapse in={filtersOpen}>
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={700}
              >
                Advanced Filters
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Exact-match filters for stock identity.
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={() =>
                setFiltersOpen(false)
              }
            >
              <CloseOutlined />
            </IconButton>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Grid
            container
            spacing={2}
          >
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                size="small"
                label="Stock Master ID"
                value={filters.id}
                onChange={handleFilterChange("id")}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                size="small"
                label="UID"
                value={filters.uid}
                onChange={handleFilterChange("uid")}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                size="small"
                label="IMEI"
                value={filters.imei}
                onChange={handleFilterChange("imei")}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                size="small"
                label="ICCID"
                value={filters.iccid_primary}
                onChange={handleFilterChange(
                  "iccid_primary"
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                size="small"
                label="Device Model Code"
                value={filters.device_model_code}
                onChange={handleFilterChange(
                  "device_model_code"
                )}
              />
            </Grid>

            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={includeDeleted}
                    onChange={(event) =>
                      setIncludeDeleted(
                        event.target.checked
                      )
                    }
                  />
                }
                label="Include deleted records"
              />

              <Stack
                direction="row"
                spacing={1}
              >
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                >
                  Clear
                </Button>

                <Button
                  variant="contained"
                  startIcon={
                    <FilterAltOutlined />
                  }
                  onClick={handleApplyFilters}
                >
                  Apply Filters
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>


      {/* ======================================================
          Report Table
      ====================================================== */}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {/* Table Header */}

        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={700}
              >
                Stock Inventory
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                {total.toLocaleString("en-IN")} records found
              </Typography>
            </Box>

            {includeDeleted && (
              <Chip
                size="small"
                label="Deleted included"
                color="warning"
                variant="outlined"
              />
            )}
          </Stack>
        </Box>


        {/* Loading */}

        {loading && (
          <Box
            sx={{
              height: 4,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                backgroundColor: "primary.main",
                animation:
                  "loadingBar 1.2s infinite",
                "@keyframes loadingBar": {
                  "0%": {
                    transform:
                      "translateX(-100%)",
                  },
                  "100%": {
                    transform:
                      "translateX(100%)",
                  },
                },
              }}
            />
          </Box>
        )}


        <TableContainer
          sx={{
            maxHeight: "calc(100vh - 420px)",
            minHeight: 420,
          }}
        >
          <Table
            stickyHeader
            size="small"
          >
            <TableHead>
              <TableRow>

                <TableCell
                  sx={{
                    minWidth: 80,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  ID
                </TableCell>

                <TableCell
                  sx={{
                    minWidth: 155,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  IMEI
                </TableCell>

                <TableCell
                  sx={{
                    minWidth: 155,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  ICCID
                </TableCell>

                <TableCell
                  sx={{
                    minWidth: 120,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  MSISDN
                </TableCell>

                <TableCell
                  sx={{
                    minWidth: 125,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  Firmware
                </TableCell>

                <TableCell
                  sx={{
                    minWidth: 180,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  Location
                </TableCell>

                <TableCell
                  sx={{
                    minWidth: 115,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  Connection
                </TableCell>

                <TableCell
                  sx={{
                    minWidth: 170,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  Last Online
                </TableCell>

                <TableCell
                  sx={{
                    minWidth: 125,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  eSIM
                </TableCell>

                <TableCell
                  sx={{
                    minWidth: 145,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  Commercial
                </TableCell>

                <TableCell
                  sx={{
                    minWidth: 145,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  Plan Expiry
                </TableCell>

                <TableCell
                  sx={{
                    minWidth: 145,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  Card Expiry
                </TableCell>

              </TableRow>
            </TableHead>


            <TableBody>

              {!loading &&
                rows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={12}
                      align="center"
                    >
                      <Box
                        sx={{
                          py: 8,
                        }}
                      >
                        <Inventory2Outlined
                          sx={{
                            fontSize: 48,
                            color: "text.disabled",
                            mb: 1,
                          }}
                        />

                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                        >
                          No stock records found
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Try changing your search
                          or filter criteria.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}


              {rows.map((row) => {
                const stage =
                  row?.location?.stage;

                const stageConfig =
                  getStageConfig(stage);

                const StageIcon =
                  stageConfig.icon;

                return (
                  <TableRow
                    key={
                      row?.stock_master_id
                    }
                    hover
                    sx={{
                      "&:last-child td": {
                        borderBottom: 0,
                      },
                    }}
                  >

                    {/* ID */}

                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                      >
                        {row?.stock_master_id ??
                          "—"}
                      </Typography>
                    </TableCell>


                    {/* IMEI */}

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily:
                            "monospace",
                          whiteSpace:
                            "nowrap",
                        }}
                      >
                        {row?.imei || "—"}
                      </Typography>
                    </TableCell>


                    {/* ICCID */}

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily:
                            "monospace",
                          whiteSpace:
                            "nowrap",
                        }}
                      >
                        {row?.iccid || "—"}
                      </Typography>
                    </TableCell>


                    {/* MSISDN */}

                    <TableCell>
                      {row?.msisdn || "—"}
                    </TableCell>


                    {/* Firmware */}

                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          row?.firmware_version ||
                          "—"
                        }
                        variant="outlined"
                      />
                    </TableCell>


                    {/* Location */}

                    <TableCell>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                      >
                        <StageIcon
                          sx={{
                            fontSize: 20,
                          }}
                        />

                        <Box>
                          <Chip
                            size="small"
                            label={
                              stageConfig.label
                            }
                            color={
                              stageConfig.color
                            }
                            variant="outlined"
                            sx={{
                              mb: 0.3,
                            }}
                          />

                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            sx={{
                              maxWidth: 150,
                              overflow: "hidden",
                              textOverflow:
                                "ellipsis",
                              whiteSpace:
                                "nowrap",
                            }}
                          >
                            {row?.location?.name ||
                              (stage ===
                              "in_transit"
                                ? "Pending manufacturer accept"
                                : "—")}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>


                    {/* Online */}

                    <TableCell>
                      {row?.online ? (
                        <Chip
                          size="small"
                          icon={
                            <CloudDoneOutlined />
                          }
                          label="Online"
                          color="success"
                        />
                      ) : (
                        <Chip
                          size="small"
                          icon={
                            <CloudOffOutlined />
                          }
                          label="Offline"
                          color="default"
                          variant="outlined"
                        />
                      )}
                    </TableCell>


                    {/* Last Online */}

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace:
                            "nowrap",
                        }}
                      >
                        {formatDateTime(
                          row?.last_online
                        )}
                      </Typography>
                    </TableCell>


                    {/* eSIM */}

                    <TableCell>
                      {row?.esim_status ? (
                        <Chip
                          size="small"
                          label={
                            row.esim_status
                          }
                          color={
                            getEsimStatusColor(
                              row.esim_status
                            )
                          }
                          variant="outlined"
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          —
                        </Typography>
                      )}
                    </TableCell>


                    {/* Commercial Activation */}

                    <TableCell>
                      {row?.commercial_activation_status ? (
                        <Chip
                          size="small"
                          label={
                            row.commercial_activation_status
                          }
                          color={
                            getCommercialStatusColor(
                              row.commercial_activation_status
                            )
                          }
                          variant="outlined"
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>


                    {/* Commercial Plan Expiry */}

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace:
                            "nowrap",
                        }}
                      >
                        {formatDate(
                          row?.esim_expiry_date
                        )}
                      </Typography>
                    </TableCell>


                    {/* Card Expiry */}

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace:
                            "nowrap",
                        }}
                      >
                        {formatDate(
                          row?.esim_card_expiry_date
                        )}
                      </Typography>
                    </TableCell>

                  </TableRow>
                );
              })}

            </TableBody>
          </Table>
        </TableContainer>


        {/* Pagination */}

        <Divider />

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={pageSize}
          onRowsPerPageChange={
            handleRowsPerPageChange
          }
          rowsPerPageOptions={
            PAGE_SIZE_OPTIONS
          }
          labelRowsPerPage="Rows per page:"
        />

      </Paper>


      {/* ======================================================
          Snackbar
      ====================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            minWidth: 320,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};


export default StockStatusReport;